import { memo } from 'react';
import { normalizeValue, viridisColor, viridisTextColor } from '@/lib/viridis';
import type { ExposureRow } from '@/store/heatmapStore';
import { TimeSpentCell } from './TimeSpentCell';
import type { TimeSpentCellValue } from './timeSpentColumn.utils';

interface DashboardHeatmapRowProps {
  row: ExposureRow;
  rowIndex: number;
  expirations: readonly string[];
  colorMin: number;
  colorMax: number;
  isSpotRow: boolean;
  kingExpiration: string | null;
  isFlashing: boolean;
  /** Null drops the Time Spent % cell. */
  timeSpent: TimeSpentCellValue | null;
  maxTimeSpentPercent: number;
}

const CELL_CLASS_NAME =
  'relative h-[calc(var(--hm-row)-1px)] border-b border-r border-[rgba(10,10,10,0.22)] p-0 text-center font-mono text-[12.5px] transition-[filter] duration-100 hover:z-[2] hover:outline hover:outline-1 hover:-outline-offset-1 hover:outline-white/60 hover:brightness-125 min-[721px]:text-[13.5px]';

export const DashboardHeatmapRow = memo(function DashboardHeatmapRow({
  row,
  rowIndex,
  expirations,
  colorMin,
  colorMax,
  isSpotRow,
  kingExpiration,
  isFlashing,
  timeSpent,
  maxTimeSpentPercent,
}: DashboardHeatmapRowProps) {
  return (
    <tr
      className={isFlashing ? 'heatmap-row-flash' : undefined}
      data-spot-row={isSpotRow || undefined}
    >
      <td className="h-[var(--hm-row)] p-0 max-[720px]:sticky max-[720px]:left-0 max-[720px]:z-[3] max-[720px]:bg-panel">
        <div
          className={`flex items-center pl-2.5 font-mono text-[13px] min-[721px]:text-sm ${
            isSpotRow
              ? 'mb-[2px] mt-[3px] h-[calc(var(--hm-row)-6px)] rounded-l bg-[#f4f4f5] font-medium text-[#0a0a0a] [clip-path:polygon(0_0,calc(100%-10px)_0,100%_50%,calc(100%-10px)_100%,0_100%)]'
              : 'h-[calc(var(--hm-row)-1px)] text-softer'
          }`}
        >
          {row.strike.toFixed(1)}
        </div>
      </td>
      {expirations.map((expiration) => {
        const cell = row.cells[expiration];
        if (cell == null) {
          return <td key={expiration} className={CELL_CLASS_NAME} />;
        }

        const t = normalizeValue(cell.raw, colorMin, colorMax);
        const textColor = viridisTextColor(t);
        const isKing = kingExpiration === expiration;

        return (
          <td
            key={expiration}
            data-row={rowIndex}
            data-expiration={expiration}
            data-king={isKing || undefined}
            className={`${CELL_CLASS_NAME} ${isKing ? 'font-bold shadow-[inset_0_0_0_2px_rgba(168,85,247,0.6)]' : ''}`}
            style={{ backgroundColor: viridisColor(t), color: textColor }}
          >
            {cell.formatted}
            {isKing && <span className="ml-0.5 inline-block text-[9px]">★</span>}
          </td>
        );
      })}
      {timeSpent != null && (
        <td className="h-[var(--hm-row)] p-0">
          <TimeSpentCell value={timeSpent} rowIndex={rowIndex} maxPercent={maxTimeSpentPercent} />
        </td>
      )}
    </tr>
  );
});
